/**
 * Projects the host's text-model list (`GET /v1/models`) into the catalog a
 * plugin receives in its bridge init payload (`FiasTextModel`, read with the
 * SDK's `useTextModels()`).
 *
 * This is init-payload data — a transport exception, not a capability (see
 * docs/capability-placement.md § Transport exceptions): nothing to invoke,
 * meter or dispatch. Invoking a model still goes through `entity_invoke`,
 * which resolves and bills server-side; this list only lets a plugin render a
 * picker without hardcoding model ids.
 *
 * Prices come from `getModelCostInfo`, the same computation the platform's own
 * model pickers (`ModelCostLabel`) use, so a plugin's label and the host's can
 * never disagree. They are display values: billing never reads them.
 */
import { useMemo } from 'react';
import type { FiasTextModel } from '@fias/arche-sdk';
import type { ModelEntity } from '@/lib/api';
import { getModelCostInfo } from '@/lib/model-cost';
import { useModels } from '@/lib/hooks/useModels';

export function toPluginTextModel(model: ModelEntity): FiasTextModel {
  const cost = getModelCostInfo(model.pricing);
  const perToken =
    cost?.kind === 'per_token' &&
    cost.credits.inputPerMillionTokens !== undefined &&
    cost.credits.outputPerMillionTokens !== undefined
      ? {
          inputCreditsPerMillionTokens: cost.credits.inputPerMillionTokens,
          outputCreditsPerMillionTokens: cost.credits.outputPerMillionTokens,
        }
      : null;
  return {
    entityId: model.entityId,
    displayName: model.name,
    provider: model.provider,
    providerDisplayName: model.providerDisplayName,
    tier: model.tier,
    recommended: model.isRecommended,
    capabilities: {
      vision: model.capabilities.supportsVision === true,
      // Same rule the server applies before honouring `parameters.reasoning`
      // (`capabilitiesSupportReasoning`), so the flag never promises a
      // control the platform would ignore.
      reasoning:
        model.capabilities.supportsThinking === true ||
        model.capabilities.isReasoningModel === true,
    },
    maxOutputTokens: model.capabilities.maxOutputTokens,
    pricing: perToken,
  };
}

export function toPluginTextModels(models: readonly ModelEntity[]): FiasTextModel[] {
  return models.map(toPluginTextModel);
}

/**
 * The catalog a host hands a plugin: the same cached `/v1/models` query the
 * platform's own pickers use, fetched only when the plugin can invoke a model.
 * `null` while loading — the host then pushes it with `sendTextModelsUpdate()`
 * once it arrives. Shared by every host (published, builder preview, dev
 * preview) so `useTextModels()` behaves the same in all of them.
 */
export function usePluginTextModelCatalog(canInvokeModels: boolean): FiasTextModel[] | null {
  const { models, loading } = useModels({ autoFetch: canInvokeModels });
  return useMemo(() => (loading ? null : toPluginTextModels(models)), [models, loading]);
}
